import SpotifyAPI from "./spotifyapi";

class Auth {
  isAuthenticated;

  constructor() {
    this.isAuthenticated = false;
  }

  // generate random string (state & code_verifier)
  static generateRandomString = (length) => {
    const validChars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    array = array.map((x) => validChars.charCodeAt(x % validChars.length));
    const randomString = String.fromCharCode.apply(null, array);
    return randomString;
  };

  // erase all session data (refresh_token // access_token)
  static clearSession = async () => {
    sessionStorage.clear();
    this.isAuthenticated = false;
  };

  // redirect to /authorize endpoint with PKCE
  static loginWithRedirect = async () => {
    const state = this.generateRandomString(10);
    sessionStorage.setItem("state", state);
    const redirect_uri = "https://spotify-app-flax.vercel.app";
    //const redirect_uri = "http://localhost:3000";
    const code_verifier = await this.generateRandomString(60);
    const code_challenge = await this.pkceChallengeFromVerifier(code_verifier);
    const client_id = "53c661eb9f2a48b5b81a9d1b3fd7702f";
    sessionStorage.setItem("code_verifier", code_verifier);
    window.location.replace(
      `https://accounts.spotify.com/authorize?response_type=code&scope=streaming user-read-private user-read-email user-library-read playlist-read-collaborative playlist-modify-public playlist-read-private playlist-modify-private&redirect_uri=${redirect_uri}&state=${state}&code_challenge=${code_challenge}&code_challenge_method=S256&client_id=${client_id}`
    );
  };

  // handle redirection from IDP
  static handleRedirect = async () => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });
    if (params.state === null) return;
    if (sessionStorage.state === params.state) {
      try {
        const response = await fetch("https://accounts.spotify.com/api/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "authorization_code",
            code: params.code,
            redirect_uri: "https://spotify-app-flax.vercel.app",
            //redirect_uri: "http://localhost:3000",
            client_id: "53c661eb9f2a48b5b81a9d1b3fd7702f",
            code_verifier: sessionStorage.code_verifier,
          }),
        });
        if (response.status === 200) {
          const data = await response.json();
          sessionStorage.clear();
          sessionStorage.setItem("access_token", data.access_token);
          sessionStorage.setItem("refresh_token", data.refresh_token);
          sessionStorage.setItem(
            "expires_at",
            Math.floor(Date.now() / 1000) + data.expires_in - 1
          );
          sessionStorage.setItem(
            "user_id",
            (await SpotifyAPI.getSpotifyUser()).id
          );
          this.isAuthenticated = true;
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        } else {
          console.log(
            "Error trying to retrieve an access token: " + response.statusText
          );
        }
        return;
      } catch (err) {
        console.log("Error trying to retrieve an access token", err);
      }
    } else {
      // if state doesn't match, perhaps redirect to another error page
      console.log("state mismatch");
      this.isAuthenticated = false;
      sessionStorage.removeItem("state");
      sessionStorage.removeItem("code_verifier");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  static pkceChallengeFromVerifier = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hashed = await window.crypto.subtle.digest("SHA-256", data);
    const challenge = btoa(
      String.fromCharCode.apply(null, new Uint8Array(hashed))
    )
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    return challenge;
  };

  static getAccessToken = async () => {
    const expires_at = sessionStorage.expires_at;
    if (Math.floor(Date.now() / 1000) >= expires_at) {
      try {
        const response = await fetch("https://accounts.spotify.com/api/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: "53c661eb9f2a48b5b81a9d1b3fd7702f",
            refresh_token: sessionStorage.refresh_token,
          }),
        });
        if (response.status === 200) {
          const data = await response.json();
          sessionStorage.setItem("access_token", data.access_token);
          sessionStorage.setItem("refresh_token", data.refresh_token);
          sessionStorage.setItem(
            "expires_at",
            Math.floor(Date.now() / 1000) + data.expires_in - 1
          );
          sessionStorage.setItem(
            "user_id",
            (await SpotifyAPI.getSpotifyUser()).id
          );
          return data.access_token;
        } else {
          console.log("failed to refresh access_token: " + response.statusText);
          sessionStorage.clear();
          this.isAuthenticated = false;
          return "";
        }
      } catch (error) {
        console.log("failed to refresh access_token", error);
      }
    } else {
      if (sessionStorage.access_token) return sessionStorage.access_token;
      else {
        sessionStorage.clear();
        this.isAuthenticated = false;
        return "";
      }
    }
  };

  static checkSession = () => {
    if (sessionStorage.refresh_token) {
      this.isAuthenticated = true;
      return true;
    } else {
      this.isAuthenticated = false;
      return false;
    }
  };

  static checkIsAuthenticated = () => {
    this.checkSession();
    return this.isAuthenticated;
  };
}

export default Auth;
